import React, { useEffect, useState } from 'react'
import { difference } from 'lodash'
import type { PostTimeline } from '../../../shared/domain/posts'
import type { CommonDeps } from '../base/CommonDeps'
import { CommentTimeline, type AfterTimelineItemComponent } from './CommentTimeline'
import { CommentOptions } from './CommentOptions'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import type { Team } from '../../../shared/domain/teams'
import { asyncDoNothing } from '../../../shared/lib/functional/function.no-deps'
import type { RichTextParagraph } from '../../../shared/domain/richText/types.no-deps'
import { CommentAdder } from './CommentAdder'
import type { RichTextComment } from '../../../shared/domain/postComments'
import type { CamelCaseFlags } from '../../../shared/cfg/flags.no-deps'
import { Pulse } from '../ui/Pulse'
import type { PostEvent } from '../../../shared/domain/postEvents/types.no-deps'
import { Placeholder } from '../ui/Placeholder'
import type { UserProfile } from '../../../shared/domain/users/types.no-deps'
import { useInterval } from '../util/interval'
import i18next from 'i18next'
import ErrorBoundary from '../error/ErrorBoundary'
import { BlockQuote } from '../ui/BlockQuote'
import { IconButton } from '../ui/IconButton'
import type { QuotedElement } from '../flow/ElementOptions/ElementOptions'
import { CommentAdderNotice, CommentNotice } from './CommentNotice'
import { useScrollIntoView } from '../util/scrollIntoView'
import { timelineCommentDomId } from './TimelineComment'
import { elementDisplayName } from '../../../shared/domain/flows/element'

const POLL_INTERVAL = 30000

export type Props = {
    postId: UUID
    profiles: UserProfile[]
    taggableUserIds: number[]
    quotedElement?: QuotedElement
    onClearQuotedElement?: () => void
    commonDeps: CommonDeps
    featureFlags: Pick<CamelCaseFlags, 'reportCommentsTranslation'>
    focusedComment?: UUID
    commentOptionsContextMenu?: boolean
    markNotificationsAsRead?: boolean
    syncNotifications?: () => Promise<void>
    collaboratorUserIds?: number[]
    showNonCollaboratorNotices?: boolean
    onUserClick?: (userId: number) => void
    onEventClick?: (event: PostEvent) => void
    onNewActionClick?: () => void
    onQuoteClick?: (comment: RichTextComment) => void
    isQuoteClickable?: (comment: RichTextComment) => boolean
    onCommentCountChanged?: (count: number) => void
    /*
    Events can be created outside this component, so update this key to force a refetch of the timeline.
    Ideally we would hold the timeline data at a same or higher level than these updates, but they are
    too coupled to higher level (and elm) code for it to be viable at this stage.
    */
    timelineLastUpdated?: Date
}

export function CommentSection({
    commonDeps,
    postId,
    quotedElement,
    profiles,
    taggableUserIds,
    featureFlags,
    collaboratorUserIds,
    focusedComment,
    showNonCollaboratorNotices = false,
    onEventClick,
    onUserClick,
    onNewActionClick,
    onClearQuotedElement,
    onCommentCountChanged,
    onQuoteClick,
    isQuoteClickable,
    syncNotifications,
    timelineLastUpdated,
    commentOptionsContextMenu,
    markNotificationsAsRead,
}: Props) {
    const [nonCollaboratorProfiles, setNonCollaboratorProfiles] = useState<UserProfile[]>([])
    const { timeline, createComment, deleteComment, updateComment } = useTimeline({
        postId,
        commonDeps,
        allowTranslation: featureFlags.reportCommentsTranslation,
        lastUpdated: timelineLastUpdated,
        onCommentCountChanged,
        quotedElement,
        focusedComment,
    })

    const onTaggedUsersChange = (taggedUserIds: number[]) => {
        const nonCollaboratorTaggedIds = difference(taggedUserIds, collaboratorUserIds)

        setNonCollaboratorProfiles(profiles.filter((entry) => nonCollaboratorTaggedIds.includes(entry.userId)))
    }

    return (
        <div className={'AppCore__commentTimeline__CommentSection'}>
            <CommentSectionBody
                commonDeps={commonDeps}
                featureFlags={featureFlags}
                timeline={timeline}
                profiles={profiles}
                commentOptionsContextMenu={commentOptionsContextMenu}
                markNotificationsAsRead={markNotificationsAsRead}
                deleteComment={deleteComment}
                updateComment={updateComment}
                onUserClick={onUserClick}
                onEventClick={onEventClick}
                syncNotifications={syncNotifications}
                onQuoteClick={onQuoteClick}
                isQuoteClickable={isQuoteClickable}
                commentTimelineFooter={
                    showNonCollaboratorNotices && (
                        <CommentNotice message={i18next.t('appcore:TimelineComment.nonCollaboratorsNotice.comment')} />
                    )
                }
                showVerticalLine={!showNonCollaboratorNotices}
            />
            <CommentSectionFooter
                commonDeps={commonDeps}
                profiles={profiles}
                taggableUserIds={taggableUserIds}
                quotedElement={quotedElement}
                createComment={async (commentText: RichTextParagraph[]) => {
                    await createComment(commentText)
                    onClearQuotedElement()
                }}
                onNewActionClick={onNewActionClick}
                onClearQuotedElement={onClearQuotedElement}
                onTaggedUsersChange={onTaggedUsersChange}
                disabled={!timeline}
                commentAdderTopContent={
                    collaboratorUserIds &&
                    showNonCollaboratorNotices && (
                        <CommentAdderNotice nonCollaboratorProfiles={nonCollaboratorProfiles} />
                    )
                }
            />
        </div>
    )
}

type BodyProps = {
    commonDeps: CommonDeps
    featureFlags: Pick<CamelCaseFlags, 'reportCommentsTranslation'>
    timeline: PostTimeline | null
    profiles: UserProfile[]
    commentOptionsContextMenu?: boolean
    markNotificationsAsRead?: boolean
    deleteComment: (commentId: UUID) => Promise<void>
    updateComment: (commentId: UUID, commentText: RichTextParagraph[]) => Promise<void>
    onUserClick?: (userId: number) => void
    onEventClick?: (event: PostEvent) => void
    syncNotifications?: () => Promise<void>
    onQuoteClick?: (comment: RichTextComment) => void
    isQuoteClickable?: (comment: RichTextComment) => boolean
    showVerticalLine?: boolean
    commentTimelineFooter?: React.ReactElement

    AfterTimelineItemContent?: AfterTimelineItemComponent
}

export function CommentSectionBody({
    commonDeps,
    featureFlags,
    timeline,
    profiles,
    commentOptionsContextMenu,
    markNotificationsAsRead,
    deleteComment,
    updateComment,
    onUserClick,
    onEventClick,
    syncNotifications,
    onQuoteClick,
    isQuoteClickable,
    showVerticalLine,
    commentTimelineFooter,

    AfterTimelineItemContent,
}: BodyProps) {
    const [optionsCommentId, setOptionsCommentId] = useState<UUID | null>(null)
    const [editCommentId, setEditCommentId] = useState<UUID | null>(null)
    const [offline, setOffline] = useState<boolean>(false)

    const teamNames = useTeamNames(commonDeps)
    useEffect(() => {
        commonDeps.isOnline().then((online) => setOffline(!online))
    }, [])

    if (offline) {
        return <Placeholder message={i18next.t('appcore:TimelineComment.offline')} icon={'cloud-cross'} />
    }

    if (!timeline || !teamNames) {
        return <Pulse />
    }

    if (timeline.length === 0) {
        return <Placeholder icon={'wave'} message={i18next.t('appcore:TimelineComment.beFirstToComment')} />
    }

    const closeCommentOptions = () => setOptionsCommentId(null)
    const closeCommentEdit = () => setEditCommentId(null)
    const submitCommentEdit = (commentText: RichTextParagraph[]) => {
        updateComment(editCommentId, commentText)
        closeCommentEdit()
    }
    const startEditingComment = () => {
        setOptionsCommentId(null)
        setEditCommentId(optionsCommentId)
    }

    const editingData = editCommentId && {
        commentId: editCommentId,
        onTextSave: submitCommentEdit,
        onCancel: closeCommentEdit,
    }
    const contextMenuData = optionsCommentId && {
        commentId: optionsCommentId,
        onDelete: () => deleteComment(optionsCommentId),
        onEdit: startEditingComment,
        displayMenu: true,
    }

    return (
        <ErrorBoundary deps={commonDeps} fallback={null}>
            <CommentTimeline
                deps={commonDeps}
                userId={commonDeps.user.id}
                items={timeline}
                profiles={Promise.resolve(profiles)}
                teamNames={teamNames}
                onCommentActionClick={setOptionsCommentId}
                showContextMenu={commentOptionsContextMenu}
                showVerticalLine={showVerticalLine}
                featureFlags={featureFlags}
                editingCommentTextData={editingData}
                contextMenuData={contextMenuData}
                fetchTranslation={(comment) => translateComment(comment, commonDeps)}
                routeToUserPage={onUserClick}
                onEventClick={onEventClick}
                markNotificationsAsRead={markNotificationsAsRead}
                syncNotifications={syncNotifications}
                onQuoteClick={onQuoteClick}
                isQuoteClickable={isQuoteClickable}
                footerContent={commentTimelineFooter}
                AfterItemContent={AfterTimelineItemContent}
            />
            {commentOptionsContextMenu || (
                <CommentOptions
                    open={optionsCommentId !== null}
                    onClose={closeCommentOptions}
                    onDelete={() => deleteComment(optionsCommentId)}
                    onEdit={startEditingComment}
                />
            )}
        </ErrorBoundary>
    )
}

type FooterProps = {
    commonDeps: CommonDeps
    profiles: UserProfile[]
    taggableUserIds: number[]
    disabled?: boolean
    quotedElement?: QuotedElement
    commentAdderTopContent?: React.ReactElement
    createComment: (commentText: RichTextParagraph[]) => Promise<void>
    onNewActionClick?: () => void
    onClearQuotedElement?: () => void
    onTaggedUsersChange?: (userIds: number[]) => void
}

export function CommentSectionFooter({
    commonDeps,
    profiles,
    taggableUserIds,
    quotedElement,
    disabled,
    commentAdderTopContent,
    createComment,
    onNewActionClick,
    onClearQuotedElement,
    onTaggedUsersChange,
}: FooterProps) {
    const authorProfile = profiles.find(({ userId }) => userId === commonDeps.user.id)
    const taggableProfiles = profiles.filter(({ userId }) => taggableUserIds.includes(userId))

    return (
        <ErrorBoundary deps={commonDeps} fallback={null}>
            {quotedElement && (
                <QuotePreview quote={elementDisplayName(quotedElement)} onCancel={onClearQuotedElement} />
            )}
            <CommentAdder
                disabled={disabled}
                onSubmit={createComment}
                profiles={taggableProfiles}
                authorProfile={authorProfile}
                language={commonDeps.language}
                topContent={commentAdderTopContent}
                onAction={onNewActionClick}
                onTaggedUsersChange={onTaggedUsersChange}
            />
        </ErrorBoundary>
    )
}

type QuotePreviewProps = {
    onCancel: () => void
    quote: string
}

function QuotePreview({ onCancel, quote }: QuotePreviewProps) {
    return (
        <div className={'AppCore__commentTimeline__QuotePreview'}>
            <BlockQuote>{quote}</BlockQuote>
            <IconButton iconName={'delete-cross-in-circle'} size={'tiny'} onClick={onCancel} border={false} />
        </div>
    )
}

type TimelineOptions = {
    postId: UUID
    quotedElement?: QuotedElement
    commonDeps: CommonDeps
    allowTranslation: boolean
    focusedComment?: UUID
    lastUpdated?: Date
    onCommentCountChanged?: (count: number) => void
}

type TimelineResult = {
    timeline: PostTimeline | null
    createComment: (commentText: RichTextParagraph[]) => Promise<void>
    deleteComment: (commentId: UUID) => Promise<void>
    updateComment: (commentId: UUID, commentText: RichTextParagraph[]) => Promise<void>
}

type WithTimelineProps = {
    options: TimelineOptions
    children: (result: TimelineResult) => JSX.Element
}

/** Wraps the hook that fetches and updates the comment timeline for use
in old class components */
export function WithTimeline({ options, children }: WithTimelineProps) {
    const result = useTimeline(options)

    return children(result)
}

function useTimeline({
    postId,
    quotedElement,
    commonDeps,
    allowTranslation,
    lastUpdated,
    onCommentCountChanged,
    focusedComment,
}: TimelineOptions): TimelineResult {
    const { httpClient } = commonDeps

    const [timeline, setTimeline] = useState<PostTimeline | null>(null)

    const fetchComments = () =>
        httpClient
            .getCommentTimeline({
                postId,
                version: 2,
                richText: true,
                includeLanguage: allowTranslation,
            })
            .then((r) => r.timeline as PostTimeline)
            .then(setTimeline)

    useInterval(fetchComments, POLL_INTERVAL)
    useScrollIntoView(focusedComment && timelineCommentDomId(focusedComment))

    const commentCount = timelineCommentCount(timeline)

    useEffect(() => {
        fetchComments()
    }, [postId, lastUpdated])

    useEffect(() => {
        if (onCommentCountChanged) {
            onCommentCountChanged(commentCount)
        }
    }, [commentCount])

    if (!timeline) {
        return {
            timeline: null,
            createComment: asyncDoNothing,
            deleteComment: asyncDoNothing,
            updateComment: asyncDoNothing,
        }
    }

    const createComment = async (commentText: RichTextParagraph[]) => {
        const comment = await httpClient
            .addComments(postId, commentText, {
                includeLanguage: allowTranslation,
                workflowElement: quotedElement?.id,
                loopPath: quotedElement?.loopPath,
            })
            .then((r) => r.comment)
        setTimeline([
            ...timeline,
            {
                type: 'comment',
                comment: comment as RichTextComment,
                quotedElement,
            },
        ])
    }

    const deleteComment = async (commentId: UUID) => {
        await httpClient.removeComment(commentId)
        setTimeline((old) => updateCommentInTimeline(old, commentId, { isDeleted: true }))
    }

    const updateComment = async (commentId: UUID, commentText: RichTextParagraph[]) => {
        const comment = await httpClient.editComment(commentId, commentText, allowTranslation).then((r) => r.comment)
        setTimeline((old) => updateCommentInTimeline(old, comment.id, comment as RichTextComment))
    }

    return { timeline, createComment, deleteComment, updateComment }
}

function useTeamNames({ httpClient }: CommonDeps) {
    const [teams, setTeams] = useState<Team[] | null>(null)

    useEffect(() => {
        httpClient
            .getTeams({})
            .then((r) => r.teams)
            .then(setTeams)
    }, [])

    if (!teams) {
        return null
    }

    const teamNames = Object.fromEntries(teams.map(({ id, name }) => [id, name]))
    return teamNames
}

async function translateComment(comment: RichTextComment, { httpClient, language }: CommonDeps) {
    const translatedComment = await httpClient
        .getPostComments({
            postId: comment.post,
            id: comment.id,
            richText: true,
            language,
        })
        .then((r) => r.comments[0])

    return translatedComment as RichTextComment
}

function updateCommentInTimeline(timeline: PostTimeline, commentId: UUID, updates: Partial<RichTextComment>) {
    return timeline.map((item) =>
        item.type === 'comment' && item.comment.id === commentId
            ? { ...item, comment: { ...item.comment, ...updates } }
            : item
    )
}

function timelineCommentCount(timeline: PostTimeline | null) {
    if (timeline === null) {
        return 0
    }
    return timeline.filter((item) => item.type === 'comment' && !item.comment.isDeleted).length
}
